import React, { useMemo, useContext } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { Flex, HStack, Hide } from '@chakra-ui/react';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

import { ButtonLink } from '@components/atoms/Button';
import LazyLoad from '@components/atoms/LazyLoad';
import SliderArrows from '@components/molecules/SliderArrows';
import SliderPagination from '@components/molecules/SliderPagination';
import Section from '@components/atoms/Section';
import Wrapper from '@components/atoms/Wrapper';
import dataLayerPush from '@helpers/dataLayerPush';
import { LocaleContext } from '@helpers/LocaleProvider';
import NewsCard from '@components/molecules/NewsCard';

const NewsCarousel = ({
  data: {
    cta,
    entries,
    tags,
    carouselCtaText,
    themeName,
    backgroundColour = null
  }
}) => {
  /**
   * Get list of latest entries in case we need them.
   */
  const {
    allStrapiArticle: { nodes: allArticles },
    allStrapiArticleListing: { nodes: allArticleListings }
  } = useStaticQuery(graphql`
    {
      allStrapiArticle(sort: { publishDate: DESC }) {
        nodes {
          publishDate
          title
          slug
          tags {
            tagName
            tagId
          }
          thumbnail {
            localFile {
              childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH)
              }
            }
          }
        }
      }
      allStrapiArticleListing {
        nodes {
          locale
          slug
        }
      }
    }
  `);
  // Sets the current locale .
  const { locale } = useContext(LocaleContext);
  /*
   * Find matching locale and set the base path to constuct URL.
   */
  const articleListings = allArticleListings.find(
    (node) => node.locale === locale
  );
  const basePath = `${locale}/${articleListings.slug}`;

  /**
   * Determine whether to use the latest entries or custom list.
   */
  const entriesList = useMemo(() => {
    // Filter down the latest entries by tag if one has been selected.
    if (tags && tags.length > 0) {
      return allArticles.filter((entry) => {
        const filteredEntries = entry.tags.filter(
          ({ tagId }) => tags.some((selectedTag) => tagId === selectedTag.tagId)
          // eslint-disable-next-line function-paren-newline
        );

        return filteredEntries.length > 0;
      });
    }

    // Use the latest entries when no custom list is entered
    if (entries.length === 0) {
      return allArticles.slice(0, 6);
    }

    return entries;
  }, [entries, tags, allArticles]);
  return (
    <Section
      bg={backgroundColour}
      py={{ base: 12, md: 24 }}
      overflow="hidden"
      sx={{
        '.swiper': {
          py: 1, // to prevent overflow hidden cropping focus state on buttons.
          overflow: `visible`
        },
        '.swiper-wrapper': {
          width: `fit-content` // to ensure correct width is set on slider wrapper and dragging works.
        },
        '.swiper-slide': {
          height: `auto`
        }
      }}>
      <LazyLoad>
        <Wrapper>
          <Swiper
            modules={[Navigation, Pagination]}
            navigation={{
              prevEl: `.prev`,
              nextEl: `.next`
            }}
            pagination={{ el: `.pagination`, clickable: true }}
            spaceBetween={25}
            slidesPerView={1.15}
            breakpoints={{
              600: {
                slidesPerView: 1.5,
                spaceBetween: 24
              },
              768: {
                slidesPerView: 1.75,
                spaceBetween: 24
              },
              1024: {
                slidesPerView: 2.5,
                spaceBetween: 48
              }
            }}>
            {entriesList &&
              entriesList.map((entry) => (
                <SwiperSlide key={entry.slug}>
                  <NewsCard
                    link={`/${basePath}/${entry.slug}`}
                    image={entry.thumbnail}
                    title={entry.title}
                    themeName={themeName}
                    ctaText={carouselCtaText}
                  />
                </SwiperSlide>
              ))}
            <Flex
              slot="container-end"
              align="center"
              justify={{ md: `space-between` }}
              direction={{ base: `column`, md: `row-reverse` }}
              mt={{ base: 8, md: 16 }}>
              <HStack align={{ md: `center` }} spacing={{ md: 8 }}>
                <Hide below="md">
                  <SliderArrows themeName={themeName} />
                </Hide>
                <SliderPagination />
              </HStack>
              {cta && (
                <Hide below="md">
                  <ButtonLink
                    variant="primary"
                    href={cta.link}
                    size="md"
                    mt={{ base: 8, md: 0 }}
                    onClick={() => {
                      dataLayerPush({
                        event: `cta_click`,
                        interaction: {
                          click_text: cta.title,
                          link_url: cta.link,
                          cta_type: `news_carousel`
                        }
                      });
                    }}>
                    {cta.title}
                  </ButtonLink>
                </Hide>
              )}
            </Flex>
          </Swiper>
        </Wrapper>
      </LazyLoad>
    </Section>
  );
};

export default NewsCarousel;
